/**
 * Chat History Utility
 *
 * Manages chat conversation history in localStorage.
 * Keeps up to 10 most recent conversations.
 */

import type { ChatMessage } from '../types/aiInsightsTypes'

export interface ChatConversation {
  id: string // conversationId from API
  title: string // First user message or "New Conversation"
  messages: ChatMessage[]
  lastUpdated: Date
  createdAt: Date
}

const STORAGE_KEY = 'gygpt_chat_history'
const MAX_CONVERSATIONS = 10

/**
 * Get all chat conversations from localStorage
 */
export const getChatHistory = (): ChatConversation[] => {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return []
    }

    const conversations: ChatConversation[] = JSON.parse(stored).map((conv: any) => ({
      ...conv,
      lastUpdated: new Date(conv.lastUpdated),
      createdAt: new Date(conv.createdAt),
      messages: conv.messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }))
    }))

    // Sort by lastUpdated, most recent first
    return conversations.sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime())
  } catch (error) {
    console.error('Error loading chat history:', error)
    localStorage.removeItem(STORAGE_KEY)

    return []
  }
}

/**
 * Save a conversation to history
 */
export const saveConversation = (conversation: ChatConversation): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const history = getChatHistory()

    // Remove existing conversation with same ID if it exists
    const filtered = history.filter(conv => conv.id !== conversation.id)

    // Add new/updated conversation at the beginning
    const updated = [conversation, ...filtered]

    // Keep only the most recent MAX_CONVERSATIONS
    const trimmed = updated.slice(0, MAX_CONVERSATIONS)

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed))
  } catch (error) {
    console.error('Error saving chat history:', error)
  }
}

/**
 * Update an existing conversation with new messages
 */
export const updateConversation = (conversationId: string, messages: ChatMessage[], title?: string): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const history = getChatHistory()
    const existing = history.find(conv => conv.id === conversationId)

    if (existing) {
      // Update existing conversation
      const updated: ChatConversation = {
        ...existing,
        messages,
        lastUpdated: new Date(),
        title: title || existing.title
      }
      saveConversation(updated)
    } else {
      // Create new conversation
      const newConv: ChatConversation = {
        id: conversationId,
        title: title || messages.find(m => m.role === 'user')?.content.slice(0, 50) || 'New Conversation',
        messages,
        lastUpdated: new Date(),
        createdAt: new Date()
      }
      saveConversation(newConv)
    }

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('gygpt_chat_updated'))
  } catch (error) {
    console.error('Error updating chat history:', error)
  }
}

/**
 * Delete a conversation from history
 */
export const deleteConversation = (conversationId: string): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const history = getChatHistory()
    const filtered = history.filter(conv => conv.id !== conversationId)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('gygpt_chat_updated'))
  } catch (error) {
    console.error('Error deleting chat history:', error)
  }
}

/**
 * Get a specific conversation by ID
 */
export const getConversation = (conversationId: string): ChatConversation | null => {
  const history = getChatHistory()

  return history.find(conv => conv.id === conversationId) || null
}

/**
 * Clear all chat history
 */
export const clearChatHistory = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.removeItem(STORAGE_KEY)
}
